/**
 * @file The Home page: the greeting, the toolbar, one stretched row holding the
 * year-to-date tiles and Needs me as equal cells that wrap below 260 px each,
 * My tasks full width beneath it, the forms table filling the remaining
 * height, and the saved view modals. The tiles, Needs me and My tasks show on
 * My View only, so every other tab gives the forms table the full height.
 * Consumer: the Home route. It calls `useHomeGoalProgress`, `useHomeMyTasks`
 * and `useAnalyticsConfigured` once each and hands the reads down, so no child
 * opens a listener or a callable.
 * Until analytics is configured the tiles are not rendered and Needs me gets
 * no analytics progress, so the row reflows around Needs me alone.
 * The root column has a definite width: inside the wrapping `FlexContent`, a
 * content-sized tab bar would otherwise widen the whole page past the viewport.
 * The forms table is absolutely positioned inside its cell, so the cell has no
 * content height of its own. It takes the height left in the viewport, with a
 * floor so the page scrolls to it rather than collapsing it to zero when the
 * content above fills the viewport. Below `md` (`md === false`) the floor is
 * one viewport under Navb, a full screen of cards. At `md` and up, and on
 * antd's first `{}` render, it is seven `controlHeightLG`, so a tall window
 * still fits without page scroll and a short one scrolls to a table of that height.
 */
import { FC, useRef, useEffect } from 'react';
import { Grid, theme, type InputRef } from 'antd';
import { NAVB_HEIGHT } from '#types';
import FlexBox from '../../../atoms/FlexBox';
import FlexCol from '../../../atoms/FlexCol';
import HomePageToolbar from '../HomePageToolbar/HomePageToolbar';
import HomeFormVersionsTable, {
  type HomeFormVersionsTableHandle,
} from '../HomeFormVersionsTable';
import TableViewNameModal from '../HomePageToolbar/TableViewNameModal';
import DeleteSavedViewModal from '../HomePageToolbar/DeleteSavedViewModal';
import useHomePageOrchestrator from './useHomePageOrchestrator';
import { searchTarget } from '@/keyboard';
import { useAnalyticsConfigured } from '@/analytics';
import { useHomeGoalProgress } from '../../useHomeGoalProgress';
import { HomeAnalyticsTiles } from '../HomeAnalyticsTiles';
import { useHomeMyTasks } from '../../useHomeMyTasks';
import { HomeMyTasks } from '../HomeMyTasks';
import { HomeNeedsMe } from '../HomeNeedsMe';
import { HomeGreeting } from '../HomeGreeting';

const CELL_MIN_WIDTH = 260;

const HomePageContent: FC = () => {
  const { token } = theme.useToken();
  const { md } = Grid.useBreakpoint();
  const tableRef = useRef<HomeFormVersionsTableHandle>(null);
  const searchInputRef = useRef<InputRef>(null);
  const o = useHomePageOrchestrator({ tableRef });
  const { clearSearch } = o;
  const goalProgress = useHomeGoalProgress();
  const { configured: analyticsConfigured } = useAnalyticsConfigured();
  const myTasks = useHomeMyTasks(analyticsConfigured);

  useEffect(() => {
    const unregister = searchTarget.register(() => {
      const input = searchInputRef.current;
      if (!input) return false;
      input.focus();
      return true;
    });
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === 'Escape' &&
        document.activeElement === searchInputRef.current?.input
      ) {
        clearSearch();
        searchInputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      unregister();
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [clearSearch]);

  return (
    <FlexCol stretch gap={20} style={{ width: '100%', minWidth: 0 }}>
      <HomeGreeting />
      <HomePageToolbar orchestrator={o} searchInputRef={searchInputRef} />
      {o.activeViewKind === 'myView' && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fit, minmax(${CELL_MIN_WIDTH}px, 1fr))`,
              gap: token.marginMD,
              alignSelf: 'stretch',
            }}
          >
            {analyticsConfigured && (
              <HomeAnalyticsTiles progress={goalProgress} />
            )}
            <HomeNeedsMe
              myTasks={myTasks}
              self={analyticsConfigured ? goalProgress.self : null}
            />
          </div>
          <FlexBox noGrow style={{ width: '100%' }}>
            <HomeMyTasks read={myTasks} />
          </FlexBox>
        </>
      )}
      <FlexCol
        stretch
        style={{
          flex: 1,
          minHeight: md === false ? `calc(100dvh - ${NAVB_HEIGHT}px)` : token.controlHeightLG * 7,
          minWidth: 0,
          position: 'relative',
          background: token.colorBgContainer,
        }}
      >
        <FlexCol noGap stretch style={{ position: 'absolute', inset: 0 }}>
          <HomeFormVersionsTable
            ref={tableRef}
            baseView={o.baseFormView}
            onBaseViewBecameDirty={o.handleBaseViewBecameDirty}
            isBaseViewActive={o.activeView === o.baseFormView}
            activeNamedViewSettings={o.activeNamedViewSettings}
            onNamedViewBecameDirty={o.handleNamedViewBecameDirty}
            configWithDynamicColumns={o.configWithDynamicColumns}
            data={o.effectiveData}
            isLoadingMore={o.effectiveIsLoadingMore}
            hasMore={o.effectiveHasMore}
            loadMore={o.loadMore}
            store={o.store}
            isSearchActive={o.isSearchActive}
          />
        </FlexCol>
      </FlexCol>
      <TableViewNameModal
        open={o.tableViewNameModal.open}
        title={o.tableViewNameModalTitle}
        okText={o.tableViewNameModalOkText}
        value={o.tableViewNameModal.value}
        onValueChange={o.setTableViewNameModalValue}
        onOk={() => void o.handleConfirmTableViewName()}
        onCancel={o.closeTableViewNameModal}
      />
      <DeleteSavedViewModal
        open={o.isDeleteSavedViewModalOpen}
        viewName={o.activeViewName}
        onConfirmDelete={() => void o.handleConfirmDeleteSavedView()}
        onCancel={o.closeDeleteSavedViewModal}
        loading={o.isDeletingSavedView}
      />
    </FlexCol>
  );
};

export default HomePageContent;
